import { ConnectError } from '@bufbuild/connect';
import { useState } from 'react';

import { DentalPhotoAlbum } from '@/shared/api/protocol-ts/model/dto_dental_photo_album_pb';
import { useAppDispatch } from '@/shared/hooks';
import { trackEvent } from '@/shared/lib';

import { albumModel } from '@/entities/album';
import { ModalID, modalModel } from '@/entities/modal';

import { errorToastCaller } from '@/features/errorToastCaller';

import { downloadAlbum } from '../lib';

type UseDentalPhotoAlbumActionsParams = {
  album: DentalPhotoAlbum;
  patientID: string;
};

export const useDentalPhotoAlbumActions = ({
  album,
  patientID,
}: UseDentalPhotoAlbumActionsParams) => {
  const dispatch = useAppDispatch();

  const [isDownloading, setIsDownloading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);

  const albumID = album.ID;

  const handleEditAlbum = () => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.EditAlbumModal,
        data: {
          albumID,
          albumName: album.Name,
          time: album.Time?.toDate(),
        },
      }),
    );
  };

  const handleDownloadAlbum = async () => {
    trackEvent({
      eventName: 'DentalPhotosAlbumDownloadClicked',
      properties: { album_id: albumID },
    });

    setIsDownloading(true);
    try {
      await downloadAlbum(albumID);
    } catch (error) {
      errorToastCaller(error as ConnectError);
    } finally {
      setIsDownloading(false);
    }
  };

  const openRemoveModal = () => setIsRemoveModalOpen(true);
  const closeRemoveModal = () => setIsRemoveModalOpen(false);

  const handleConfirmDeleteAlbum = async () => {
    if (!albumID) {
      return;
    }

    trackEvent({
      eventName: 'DentalPhotosAlbumRemoveClicked',
      properties: { album_id: albumID },
    });

    setIsDeleting(true);
    try {
      await dispatch(albumModel.thunks.deleteAlbum({ AlbumID: albumID }));
    } catch (error) {
      errorToastCaller(error as ConnectError);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUploadToAlbum = () => {
    if (!albumID || !patientID) {
      return;
    }

    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.DentalPhotosUpload,
        data: { albumID, patientID },
      }),
    );
  };

  const handleMobileUploadToAlbum = () => {
    if (!albumID || !patientID) {
      return;
    }

    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.MobileDentalPhotoUploadMenu,
        data: { patientID, albumID },
      }),
    );
  };

  return {
    isDownloading,
    isDeleting,
    isRemoveModalOpen,
    openRemoveModal,
    closeRemoveModal,
    handleEditAlbum,
    handleDownloadAlbum,
    handleConfirmDeleteAlbum,
    handleUploadToAlbum,
    handleMobileUploadToAlbum,
  };
};
